import { Head, router, usePage } from '@inertiajs/react';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { HexMap, type HexData } from '@/components/HexMap';
import { useRealtimeHexes } from '@/components/HexMap/useRealtimeHexes';
import { ClaimHexModal } from '@/components/ClaimHexModal';
import { HexResources, type ResourceData } from '@/components/HexResources';
import { FloatingNavButtons, HeaderNavButtons } from '@/components/FloatingTomeIcon';
import { UsernameModal } from '@/components/UsernameModal';
import { LeafCanopyHeader } from '@/components/LeafCanopyHeader';
import { type AxialCoord, getNeighbors, hexKey } from '@/lib/hexMath';

/** Data for a newly claimed hex, passed via flash session */
interface ClaimedHexData {
    id: number;
    name: string;
    terrain: string;
    q: number;
    r: number;
    resources: ResourceData;
    /** Whether this is an open hex (visual only, no resources) */
    isOpenHex?: boolean;
}

interface Props {
    hexes: HexData[];
    user: {
        id: number;
        username: string;
        hex: HexData | null;
        /** Whether user can claim additional hexes (GMs have unlimited) */
        canClaimHex: boolean;
        /** Whether user is a Game Master */
        isGm: boolean;
    };
}

/**
 * Main map page - displays the interactive hex map.
 * Uses real-time updates via Laravel Echo/Reverb.
 */
export default function MapIndex({ hexes: initialHexes, user }: Props) {
    // Get flash data for claimed hex (passed from HexController@claim)
    const { props } = usePage<{ flash?: { claimedHex?: ClaimedHexData } }>();
    const claimedHexFlash = props.flash?.claimedHex;

    // Use real-time hex updates - this will update when other users claim/update hexes
    // Also tracks recently updated hexes for visual highlighting
    const { hexes, recentlyUpdatedIds } = useRealtimeHexes(initialHexes);

    // Calculate claimable coordinates:
    // 1. Unclaimed hexes adjacent to at least one claimed hex (normal adjacency rule)
    // 2. Hexes configured by GM with is_claimable=true (always claimable regardless of adjacency)
    // Only calculate if the user can actually claim hexes (not at limit, not guest)
    // This updates automatically when hexes change (including real-time updates)
    const claimableCoords = useMemo(() => {
        // If user cannot claim hexes, return empty set (no highlighting)
        if (!user.canClaimHex) {
            return new Set<string>();
        }

        // Build a set of "owned" hex coordinate keys for fast lookup
        // Only hexes with an owner count as "claimed" for adjacency purposes
        // Hexes with owner=null (configured by GM) are NOT considered claimed
        const ownedKeys = new Set(
            hexes.filter((h) => h.owner !== null).map((h) => hexKey(h))
        );

        // Find all claimable coordinates
        const claimable = new Set<string>();

        // First, add all hexes that are explicitly marked as claimable by GM
        // These are claimable regardless of adjacency
        for (const hex of hexes) {
            if (hex.isClaimable && hex.owner === null) {
                claimable.add(hexKey(hex));
            }
        }

        // Then, find all unclaimed neighbors of owned hexes (normal adjacency rule)
        for (const hex of hexes) {
            // Only consider hexes with owners for adjacency calculation
            if (hex.owner === null) continue;

            const neighbors = getNeighbors(hex);
            for (const neighbor of neighbors) {
                const key = hexKey(neighbor);
                // Only add if not already owned by someone
                if (!ownedKeys.has(key)) {
                    claimable.add(key);
                }
            }
        }
        return claimable;
    }, [hexes, user.canClaimHex]);

    const [selectedCoord, setSelectedCoord] = useState<AxialCoord | null>(null);
    const [selectedHex, setSelectedHex] = useState<HexData | null>(null);
    const [showClaimModal, setShowClaimModal] = useState(false);
    // State for showing the claim success modal with resources
    const [claimedHex, setClaimedHex] = useState<ClaimedHexData | null>(null);
    // State for showing the username edit modal
    const [showUsernameModal, setShowUsernameModal] = useState(false);
    // Map theme preference (persisted in localStorage)
    const [mapTheme, setMapTheme] = useState<'current' | 'parchment' | 'starfield'>(() => {
        try {
            const saved = localStorage.getItem('sellune.mapTheme') as 'current' | 'parchment' | 'starfield' | null;
            return saved ?? 'current';
        } catch {
            return 'current';
        }
    });

    // Show success modal when flash data is present (skip for open hexes)
    useEffect(() => {
        if (claimedHexFlash) {
            // Skip showing the modal for open hexes - they don't have resources to display
            if (!claimedHexFlash.isOpenHex) {
                setClaimedHex(claimedHexFlash);
            }
            setShowClaimModal(false);
            setSelectedCoord(null);
            setSelectedHex(null);
        }
    }, [claimedHexFlash]);

    // Handle hex click - navigate to hex page (claimed) or show claim option (unclaimed + claimable)
    const handleHexClick = useCallback((hex: HexData | null, coord: AxialCoord) => {
        // A hex is considered "claimable" if:
        // 1. It doesn't exist at all (completely unclaimed), OR
        // 2. It exists but has no owner (configured by GM for player claiming)
        const isUnclaimedOrConfigured = !hex || hex.owner === null;

        if (isUnclaimedOrConfigured) {
            // Unclaimed or configured hex - show claim panel if:
            // 1. It's adjacent to a claimed hex (normal adjacency rule), OR
            // 2. User is a GM (can claim anywhere)
            const isClaimable = claimableCoords.has(hexKey(coord)) || user.isGm;
            if (isClaimable && user.canClaimHex) {
                setSelectedCoord(coord);
                setSelectedHex(null);
            }
            // If not claimable, do nothing (tooltip already shows the reason)
        } else {
            // Claimed hex with owner - navigate directly to the hex detail page
            router.visit(`/hex/${hex.id}`);
        }
    }, [claimableCoords, user.isGm, user.canClaimHex]);

    // Close the hex panel
    const closePanel = useCallback(() => {
        setSelectedCoord(null);
        setSelectedHex(null);
    }, []);

    // Open claim modal
    const openClaimModal = useCallback(() => {
        setShowClaimModal(true);
    }, []);

    // Close claim modal
    const closeClaimModal = useCallback(() => {
        setShowClaimModal(false);
        closePanel();
    }, [closePanel]);

    // Close success modal and navigate to the hex
    const handleSuccessClose = useCallback(() => {
        const hexId = claimedHex?.id;
        setClaimedHex(null);
        if (hexId) {
            router.visit(`/hex/${hexId}`);
        }
    }, [claimedHex]);

    // Just close the success modal without navigating
    const handleSuccessDismiss = useCallback(() => {
        setClaimedHex(null);
    }, []);

    return (
        <>
            <Head title="Map" />

            <div className="h-screen bg-slate-900 flex flex-col">
                <LeafCanopyHeader fixed>
                    <div className="flex items-center justify-between">
                        <h1 className="text-xl font-bold text-green-950 [text-shadow:_0_0_8px_rgba(255,255,255,0.8),_0_0_2px_rgba(255,255,255,1)]">Sellune</h1>
                        <nav className="flex items-center gap-4">
                            {/* GM link - only visible to Game Masters */}
                            {user.isGm && (
                                <a
                                    href="/gm"
                                    className="hidden md:inline-block text-green-950 font-bold hover:text-white transition-colors [text-shadow:_0_0_8px_rgba(255,255,255,0.8),_0_0_2px_rgba(255,255,255,1)]"
                                >
                                    GM
                                </a>
                            )}
                            {/* Desktop navigation buttons - to the left of username */}
                            <HeaderNavButtons userHex={user.hex} />
                            {/* Clickable username - opens edit modal */}
                            <button
                                type="button"
                                onClick={() => setShowUsernameModal(true)}
                                className="hidden md:inline-block text-green-950 font-bold hover:text-white transition-colors cursor-pointer [text-shadow:_0_0_8px_rgba(255,255,255,0.8),_0_0_2px_rgba(255,255,255,1)]"
                                title="Click to edit username"
                            >
                                {user.username}
                            </button>
                            <button
                                type="button"
                                onClick={() => router.post('/logout')}
                                className="hidden md:inline-block text-green-950 font-bold hover:text-white transition-colors [text-shadow:_0_0_8px_rgba(255,255,255,0.8),_0_0_2px_rgba(255,255,255,1)]"
                            >
                                Logout
                            </button>
                        </nav>
                    </div>
                </LeafCanopyHeader>

                {/* Map container - add top padding on mobile to account for fixed header */}
                <main className="flex-1 relative overflow-hidden pt-14 md:pt-0">
                    <HexMap
                        hexes={hexes}
                        onHexClick={handleHexClick}
                        recentlyUpdatedIds={recentlyUpdatedIds}
                        claimableCoords={claimableCoords}
                        backgroundTheme={mapTheme}
                        canClaimHexes={user.canClaimHex}
                        allUnclaimedClaimable={user.isGm && user.canClaimHex}
                        className="w-full h-full"
                    />

                    {/* Unclaimed hex panel - parchment themed, shows when clicking unclaimed hexes */}
                    {selectedCoord && !selectedHex && (
                        <div
                            className="absolute top-4 left-4 right-4 sm:right-auto z-30 rounded-lg p-4 sm:w-72"
                            style={{
                                background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.35), inset 0 0 30px rgba(139,90,43,0.1)',
                            }}
                        >
                            {/* Subtle aged paper edge effects */}
                            <div
                                className="absolute top-0 left-0 right-0 h-1 rounded-t-lg pointer-events-none"
                                style={{ background: 'linear-gradient(to bottom, rgba(139,90,43,0.15), transparent)' }}
                            />
                            <div
                                className="absolute bottom-0 left-0 right-0 h-1 rounded-b-lg pointer-events-none"
                                style={{ background: 'linear-gradient(to top, rgba(139,90,43,0.15), transparent)' }}
                            />

                            <div className="flex items-center justify-between mb-3">
                                <h2 className="text-lg font-serif font-semibold text-amber-900">Unclaimed Hex</h2>
                                <button
                                    onClick={closePanel}
                                    className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-200/50 hover:bg-amber-300/70 text-amber-800 hover:text-amber-900 transition-all border border-amber-600/30 hover:border-amber-600/50"
                                >
                                    <span className="text-sm font-serif leading-none">×</span>
                                </button>
                            </div>

                            <div className="text-sm text-amber-800 mb-3">
                                Coordinates: ({selectedCoord.q}, {selectedCoord.r})
                            </div>

                            <div>
                                {user.canClaimHex ? (
                                    <>
                                        {(() => {
                                            const exists = hexes.find(h => h.q === selectedCoord.q && h.r === selectedCoord.r);
                                            const allowed = !exists || exists.isClaimable;
                                            return (
                                                <button
                                                    onClick={openClaimModal}
                                                    className={`w-full py-2 px-4 rounded-lg font-medium transition-all ${allowed ? 'bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white shadow-md border border-amber-500/30' : 'bg-amber-200/50 text-amber-700/50 cursor-not-allowed border border-amber-400/30'}`}
                                                    disabled={!allowed}
                                                >
                                                    {allowed ? '✦ Claim This Hex' : 'Not Claimable'}
                                                </button>
                                            );
                                        })()}
                                        {/* Show GM indicator if user is GM and already owns hexes */}
                                        {user.isGm && user.hex && (
                                            <p className="text-purple-700 text-xs mt-2 text-center">
                                                ✨ GM: Unlimited hex claims
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-amber-800 text-sm italic">
                                        You already own a hex. You cannot claim another.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Claim hex modal */}
                    {showClaimModal && selectedCoord && (
                        <ClaimHexModal
                            coord={selectedCoord}
                            onClose={closeClaimModal}
                            isGm={user.isGm}
                            existingHex={(() => {
                                // Find existing hex at this coordinate (may be configured by GM but unclaimed)
                                const existing = hexes.find(h => h.q === selectedCoord.q && h.r === selectedCoord.r);
                                return existing ? { name: existing.name, domain: existing.domain, tileImage: existing.tileImage } : undefined;
                            })()}
                        />
                    )}

                    {/* Claim success modal - shows resources for newly claimed hex */}
                    {claimedHex && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                            <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md border border-slate-600 shadow-xl">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-bold text-white">🎉 Hex Claimed!</h2>
                                    <button
                                        onClick={handleSuccessDismiss}
                                        className="text-gray-300 hover:text-white text-xl"
                                    >
                                        ✕
                                    </button>
                                </div>

                                <div className="mb-4">
                                    <h3 className="text-lg font-semibold text-white mb-1">
                                        {claimedHex.name}
                                    </h3>
                                    <p className="text-gray-400 text-sm">
                                        Coordinates: ({claimedHex.q}, {claimedHex.r})
                                        {claimedHex.domain && (
                                            <> • <span className="capitalize">{claimedHex.domain}</span></>
                                        )}
                                    </p>
                                </div>

                                <div className="mb-4">
                                    <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-2">
                                        Resources (per turn)
                                    </h4>
                                    <HexResources resources={claimedHex.resources} compact />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={handleSuccessDismiss}
                                        className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                                    >
                                        Stay on Map
                                    </button>
                                    <button
                                        onClick={handleSuccessClose}
                                        className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                                    >
                                        View Hex
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </main>

                {/* Mobile bottom navigation - only visible on mobile */}
                <FloatingNavButtons userHex={user.hex} isMapPage={true} />

                {/* Username edit modal */}
                <UsernameModal
                    isOpen={showUsernameModal}
                    onClose={() => setShowUsernameModal(false)}
                    currentUsername={user.username}
                    mapTheme={mapTheme}
                    onMapThemeChange={(theme) => {
                        setMapTheme(theme);
                    }}
                />
            </div>
        </>
    );
}
